import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { socialAPI, pointsAPI, simhubAPI, moderationAPI, achievementConfigsAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import MemberBadge from "@/components/MemberBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Heart, MessageCircle, Send, Loader2, Bookmark, Share2, Trophy,
  Timer, Wrench, Gauge, Camera, Zap, MoreHorizontal, Plus, X, Link2,
  Flag, Trash2, Video, Users, CheckCircle2, Search, Pencil, Gamepad2,
} from "lucide-react";

const POST_TYPES = {
  achievement: { label: "Achievement", icon: Trophy, color: "hsl(45 100% 55%)" },
  race_result: { label: "Race Result", icon: Zap, color: "hsl(2 100% 55%)" },
  setup_share: { label: "Setup", icon: Wrench, color: "hsl(189 94% 55%)" },
  lap_time: { label: "Lap Time", icon: Timer, color: "hsl(145 70% 50%)" },
  rig_photo: { label: "Rig", icon: Camera, color: "hsl(280 70% 65%)" },
  status: null,
};

// Map a SimHub milestone kind to the user-facing POST_TYPES label.
// Lap-time personal-best milestones are labelled "Lap Time", everything else
// (first session, top speed, laps counts, debut, leaderboard rank) is
// labelled "Achievement" — no "Milestone" tag is ever shown.
function simhubPostTypeKey(kind = "") {
  if (kind === "track_pb") return "lap_time";
  return "achievement";
}

// Map a raw milestone_kind to an achievement-config key (matches backend).
function canonicalAchType(kind = "") {
  if (!kind) return "";
  if (kind.startsWith("laps_")) return "laps";
  if (kind.startsWith("leaderboard_top_")) return "leaderboard";
  return kind;
}

function timeAgo(iso) {
  if (!iso) return "";
  // Backend uses datetime.utcnow() which serializes without a timezone marker.
  // Without the trailing 'Z', browsers parse it as *local* time and produce
  // negative "-Xs ago" values when the user's clock is behind UTC. Append 'Z'
  // if no offset is present so the delta is always positive.
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/i.test(iso);
  const normalized = hasTz ? iso : iso + "Z";
  const s = Math.max(0, Math.floor((Date.now() - new Date(normalized).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function authorLink(post) {
  return post?.author_slug ? `/u/${post.author_slug}` : `/u/${post?.author_username || ""}`;
}

function milestoneKindLabel(kind) {
  if (!kind) return "Milestone";
  if (kind === "first_session") return "First session logged";
  if (kind === "top_speed") return "New personal top speed";
  if (kind === "track_pb") return "New track PB";
  if (kind === "track_first") return "New track unlocked";
  if (kind.startsWith("laps_")) return `${kind.split("_")[1]} laps`;
  if (kind.startsWith("leaderboard_top_")) return `Leaderboard Top ${kind.split("_").pop()}`;
  return "Milestone";
}

function milestoneSnapshotLine(ev) {
  const s = ev?.snapshot || {};
  if (ev?.kind === "track_pb") {
    const ms = s.lap_time_ms || 0;
    const m = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    const mil = ms % 1000;
    return `${s.track_name || s.track_key || "Track"} · ${m}:${String(sec).padStart(2, "0")}.${String(mil).padStart(3, "0")}${s.car_model ? ` · ${s.car_model}` : ""}`;
  }
  if (ev?.kind === "top_speed") {
    return `${(s.top_speed_kmh || 0).toFixed(1)} km/h${s.car_model ? ` · ${s.car_model}` : ""}${s.track_name ? ` at ${s.track_name}` : ""}`;
  }
  if (ev?.kind === "track_first") return `${s.track_name || s.track_key || "Track"}`;
  if (ev?.kind?.startsWith("leaderboard_top_")) {
    return `${s.track_name || s.track_key || "Track"} · P${s.rank || "?"}`;
  }
  if (ev?.kind?.startsWith("laps_")) return `${s.total_laps || s.threshold || 0} career laps`;
  return "";
}

function PostCard({ post, currentUserId, isAdmin, savedIds, onChanged, onToggleSave, achConfigs }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState(null);
  const [comment, setComment] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const liked = post.likes?.includes(currentUserId);
  const saved = savedIds?.has(post.id);
  const typeKey = post.post_type === "simhub_milestone"
    ? simhubPostTypeKey(post.milestone_kind)
    : post.post_type;
  const type = POST_TYPES[typeKey];
  const isAuthor = post.author_id === currentUserId;
  const isSimhubVerified = post.post_type === "simhub_milestone";

  const like = async () => {
    try { await socialAPI.toggleLike(post.id); onChanged(); } catch { toast.error("Could not like"); }
  };

  const loadComments = async () => {
    setExpanded(true);
    if (comments) return;
    try {
      const res = await socialAPI.getPost(post.id);
      setComments(res.data?.comments || []);
    } catch { setComments([]); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await socialAPI.createComment(post.id, { content: comment });
      setComment("");
      const res = await socialAPI.getPost(post.id);
      setComments(res.data?.comments || []);
      onChanged(true);
    } catch { toast.error("Could not comment"); }
  };

  const sharePost = async () => {
    const url = `${window.location.origin}${authorLink(post)}?post=${post.id}`;
    const payload = { title: `Post by ${post.author_username}`, text: post.content?.slice(0, 120) || "", url };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not share — copy this: " + url);
    }
  };

  const doSave = async () => {
    try {
      const res = await socialAPI.toggleSave(post.id);
      onToggleSave(post.id, res.data?.saved);
      toast.success(res.data?.saved ? "Saved for later" : "Removed from saved");
    } catch { toast.error("Could not save"); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${authorLink(post)}?post=${post.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  const submitReport = async () => {
    setReporting(true);
    try {
      await moderationAPI.report({ content_type: "social", content_id: post.id, reason: reportReason });
      toast.success("Thanks, this post was sent for review.");
      setReportOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not report");
    } finally { setReporting(false); }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await socialAPI.deletePost(post.id);
      toast.success("Post deleted");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  const saveEdit = async () => {
    try {
      const res = await socialAPI.updatePost(post.id, { content: editText.trim() });
      setEditing(false);
      if (res.data?.moderation_status === "pending") {
        toast.success("Your edited post is pending review before it goes live.");
      } else {
        toast.success("Post updated");
      }
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  return (
    <Card className="overflow-hidden border-border bg-card" data-testid={`social-post-${post.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <Link to={authorLink(post)} data-testid={`social-post-author-link-${post.id}`}>
          <Avatar className="h-9 w-9 ring-2 ring-primary/40 transition-transform hover:scale-105">
            <AvatarImage src={post.author_image} />
            <AvatarFallback className="bg-secondary text-xs">{(post.author_username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={authorLink(post)} className="hover:underline">
            <p className="truncate text-sm font-semibold leading-tight">{post.author_username}</p>
          </Link>
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground">{timeAgo(post.created_at)} ago</p>
            <MemberBadge memberCategory={post.author_member_category} isFounding={post.author_is_founding} testId={`social-member-badge-${post.id}`} />
          </div>
        </div>
        {type && (
          <Badge className="gap-1 border-0 text-[10px] font-semibold text-background" style={{ backgroundColor: type.color }}>
            <type.icon className="h-3 w-3" /> {type.label}
          </Badge>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-muted-foreground hover:text-foreground" data-testid={`social-post-menu-${post.id}`}>
              <MoreHorizontal className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={copyLink} data-testid={`social-post-menu-copy-${post.id}`}>
              <Link2 className="mr-2 h-4 w-4" /> Copy link
            </DropdownMenuItem>
            {!isAuthor && (
              <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid={`social-post-menu-report-${post.id}`}>
                <Flag className="mr-2 h-4 w-4" /> Report post
              </DropdownMenuItem>
            )}
            {(isAuthor || isAdmin) && (
              <>
                <DropdownMenuSeparator />
                {isAuthor && (
                  <DropdownMenuItem onClick={() => { setEditText(post.content || ""); setEditing(true); }} data-testid={`social-post-menu-edit-${post.id}`}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit post
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={deletePost} className="text-destructive" data-testid={`social-post-menu-delete-${post.id}`}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete post
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit post</DialogTitle></DialogHeader>
          <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={5} data-testid={`social-edit-input-${post.id}`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid={`social-edit-save-${post.id}`}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Achievement banner for milestone/achievement posts */}
      {(post.post_type === "achievement" || post.post_type === "simhub_milestone") && (post.milestone_badge || post.achievement_name) && (() => {
        const cfg = (achConfigs && achConfigs[canonicalAchType(post.milestone_kind)]) || {};
        const bgUrl = cfg.background_image ? `${process.env.REACT_APP_BACKEND_URL}${cfg.background_image}` : null;
        const title = cfg.title || "Achievement Unlocked";
        const subtitle = post.milestone_badge || post.achievement_name || cfg.subtitle || "";
        return (
          <div
            className="relative mx-3 mb-2 flex items-center gap-3 overflow-hidden rounded-md border border-primary/40 p-3"
            style={bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
            data-testid={`social-achievement-banner-${post.id}`}
          >
            {bgUrl ? <div className="absolute inset-0 bg-black/45" /> : <div className="absolute inset-0 bg-primary/10" />}
            <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/20">
              <Trophy className={`h-5 w-5 ${bgUrl ? "text-white" : "text-primary"}`} />
            </div>
            <div className="relative min-w-0 flex-1">
              <p className={`whitespace-nowrap text-[10px] font-bold uppercase tracking-wide ${bgUrl ? "text-white" : "text-primary"}`}>{title}</p>
              <p className={`truncate text-sm font-semibold ${bgUrl ? "text-white" : "text-foreground"}`}>{subtitle}</p>
            </div>
            {isSimhubVerified && (
              <span
                className="relative flex flex-shrink-0 items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent"
                data-testid={`social-simhub-verified-${post.id}`}
              >
                <CheckCircle2 className="h-3 w-3" /> SimHub verified
              </span>
            )}
          </div>
        );
      })()}

      {/* Media */}
      {post.images?.length > 0 && (
        <div
          className="relative bg-secondary"
          style={{ aspectRatio: post.image_aspect === "portrait" ? "4 / 5" : post.image_aspect === "landscape" ? "16 / 9" : "1 / 1" }}
        >
          <img src={post.images[0]} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      {/* Caption first, so the post is legible without scrolling past the action bar */}
      <div className="space-y-1 px-3 pb-2 pt-3">
        {post.content && (
          <p className="text-sm">
            <Link to={authorLink(post)} className="font-semibold hover:underline">{post.author_username}</Link>{" "}
            <span className="text-foreground/90">{post.content}</span>
          </p>
        )}
        {post.hashtags?.length > 0 && (
          <p className="text-sm text-accent">{post.hashtags.map((h) => `#${h}`).join(" ")}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 px-3 pt-1">
        <button onClick={like} className={`transition-transform active:scale-90 ${liked ? "text-primary" : "text-foreground hover:text-primary"}`} data-testid={`social-like-${post.id}`}>
          <Heart className={`h-6 w-6 ${liked ? "fill-current" : ""}`} />
        </button>
        <button onClick={loadComments} className="text-foreground hover:text-accent" data-testid={`social-comment-toggle-${post.id}`}>
          <MessageCircle className="h-6 w-6" />
        </button>
        <button onClick={sharePost} className="text-foreground hover:text-accent" data-testid={`social-share-${post.id}`}>
          <Share2 className="h-6 w-6" />
        </button>
        <button onClick={doSave} className={`ml-auto ${saved ? "text-accent" : "text-foreground hover:text-accent"}`} data-testid={`social-save-${post.id}`}>
          <Bookmark className={`h-6 w-6 ${saved ? "fill-current" : ""}`} />
        </button>
      </div>

      {/* Likes count + comment thread */}
      <div className="space-y-1 px-3 pb-3 pt-2">
        <p className="text-sm font-semibold">{post.likes?.length || 0} likes</p>
        {!expanded && post.comment_count > 0 && (
          <button onClick={loadComments} className="text-sm text-muted-foreground hover:text-foreground">
            View all {post.comment_count} comments
          </button>
        )}

        {expanded && (
          <div className="mt-2 space-y-2 border-t border-border pt-2">
            {comments === null ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : comments.length ? (
              comments.map((c) => (
                <p key={c.id} className="text-sm" data-testid={`social-comment-${c.id}`}>
                  <span className="font-semibold">{c.author_username}</span>{" "}
                  <span className="text-foreground/90">{c.content}</span>
                </p>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No comments yet — be the first.</p>
            )}
          </div>
        )}
      </div>

      {/* Add comment */}
      <div className="flex items-center gap-2 border-t border-border px-3 py-2">
        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addComment()}
          placeholder="Add a comment..."
          className="h-9 border-0 bg-transparent px-0 focus-visible:ring-0"
          data-testid={`social-comment-input-${post.id}`}
        />
        <button onClick={addComment} disabled={!comment.trim()} className="text-sm font-semibold text-primary disabled:opacity-40" data-testid={`social-comment-post-${post.id}`}>
          Post
        </button>
      </div>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="border-border bg-card">
          <DialogHeader><DialogTitle>Report this post</DialogTitle></DialogHeader>
          <Textarea placeholder="What's wrong with this post?" value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={3} data-testid={`social-post-report-reason-${post.id}`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} disabled={reporting}>Cancel</Button>
            <Button variant="destructive" onClick={submitReport} disabled={reporting} data-testid={`social-post-report-submit-${post.id}`}>
              {reporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Flag className="mr-1 h-4 w-4" /> Send report</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Social() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [postType, setPostType] = useState("status");
  const [attachedImage, setAttachedImage] = useState("");
  const [postAspect, setPostAspect] = useState("square");
  const [posting, setPosting] = useState(false);
  const [topRacers, setTopRacers] = useState([]);
  const [stories, setStories] = useState([]);
  const [storyModal, setStoryModal] = useState(null);
  const [storyForm, setStoryForm] = useState({ media_url: "", media_type: "image", caption: "" });
  const [creatingStory, setCreatingStory] = useState(false);
  const [storyViewer, setStoryViewer] = useState(null); // { username, stories: [], index }
  const [savedIds, setSavedIds] = useState(new Set());
  const [achConfigs, setAchConfigs] = useState({});
  useEffect(() => { achievementConfigsAPI.list().then((r) => setAchConfigs(r.data || {})).catch(() => {}); }, []);
  const [filter, setFilter] = useState(searchParams.get("tab") === "saved" ? "saved" : (searchParams.get("filter") || "all"));
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [milestoneDraft, setMilestoneDraft] = useState(null);
  const imageInputRef = useRef(null);
  const focusId = searchParams.get("post");
  const [focusPost, setFocusPost] = useState(null);
  const postRefs = useRef({});

  // Deep-link to a specific post (?post=<id>): fetch it if it isn't in the feed.
  useEffect(() => {
    if (!focusId || posts.some((p) => p.id === focusId)) { setFocusPost(null); return; }
    let cancelled = false;
    socialAPI.getPost(focusId)
      .then((r) => { if (!cancelled && r.data?.post) setFocusPost(r.data.post); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [focusId, posts]);

  // Scroll to and highlight the focused post once rendered.
  useEffect(() => {
    if (!focusId) return;
    const el = postRefs.current[focusId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, posts, focusPost]);

  // Prefill composer when arriving via a milestone notification (?draft=milestone-<id>)
  useEffect(() => {
    const draft = new URLSearchParams(location.search).get("draft");
    if (!draft || !draft.startsWith("milestone-")) return;
    const id = draft.slice("milestone-".length);
    if (milestoneDraft && milestoneDraft._id === id) return;
    simhubAPI.getMilestone(id).then((r) => {
      const ev = r.data;
      if (!ev || ev.shared_post_id) return;
      setMilestoneDraft(ev);
      // Do NOT seed the textarea — the user writes their own post.
      setContent("");
      setPostType("achievement");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }).catch(() => {});
  }, [location.search]);

  const load = (silent = false) => {
    if (filter === "saved") {
      socialAPI.listSaved()
        .then((res) => setPosts(res.data?.posts || []))
        .catch(() => setPosts([]))
        .finally(() => !silent && setLoading(false));
      return;
    }
    socialAPI.getFeed({ limit: 30, filter })
      .then((res) => setPosts(res.data?.posts || []))
      .catch(() => setPosts([]))
      .finally(() => !silent && setLoading(false));
  };
  const loadStories = () => {
    socialAPI.getStories()
      .then((r) => setStories(Array.isArray(r.data) ? r.data : r.data?.stories || []))
      .catch(() => setStories([]));
  };
  const openStoryViewer = async (storyUser) => {
    const uid = storyUser.user_id || storyUser.author_id;
    if (!uid) return;
    try {
      const res = await socialAPI.getUserStories(uid);
      const list = Array.isArray(res.data) ? res.data : res.data?.stories || [];
      if (list.length === 0) {
        toast.error("No active stories");
        return;
      }
      setStoryViewer({ username: storyUser.username || storyUser.author_username, stories: list, index: 0 });
    } catch {
      toast.error("Could not load stories");
    }
  };
  const storyViewerNext = () => {
    setStoryViewer((v) => {
      if (!v) return v;
      if (v.index + 1 >= v.stories.length) return null;
      return { ...v, index: v.index + 1 };
    });
  };
  const storyViewerPrev = () => {
    setStoryViewer((v) => (v && v.index > 0 ? { ...v, index: v.index - 1 } : v));
  };
  const loadSaved = () => {
    socialAPI.listSaved()
      .then((r) => setSavedIds(new Set((r.data?.posts || []).map((p) => p.id))))
      .catch(() => setSavedIds(new Set()));
  };

  // Debounced social search (keyword, @username, or #hashtag)
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      socialAPI.getPosts({ search: q })
        .then((r) => setSearchResults(r.data?.posts || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => {
    load();
    loadStories();
    loadSaved();
    pointsAPI.getLeaderboard(5).then((r) => setTopRacers(r.data?.leaderboard || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
    const params = new URLSearchParams(searchParams);
    if (filter === "saved") {
      params.set("tab", "saved");
      params.delete("filter");
    } else {
      params.delete("tab");
      if (filter && filter !== "all") params.set("filter", filter); else params.delete("filter");
    }
    setSearchParams(params, { replace: true });
  }, [filter]);

  const attachImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only images can be attached to posts"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setAttachedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const createPost = async () => {
    if (!content.trim() && !attachedImage) return;
    setPosting(true);
    try {
      if (milestoneDraft) {
        await simhubAPI.shareMilestone(milestoneDraft._id, content);
        setMilestoneDraft(null);
        // Clear the draft param
        const params = new URLSearchParams(searchParams);
        params.delete("draft");
        setSearchParams(params, { replace: true });
      } else {
        const res = await socialAPI.createPost({
          content,
          post_type: postType,
          images: attachedImage ? [attachedImage] : [],
          image_aspect: postAspect,
        });
        if (res.data?.moderation_status === "pending") {
          setContent(""); setAttachedImage(""); setPostType("status"); setPostAspect("square");
          toast.success("Your post is pending review before it goes live. Sorry for any wait; we review quickly.");
          setPosting(false);
          return;
        }
      }
      setContent("");
      setAttachedImage("");
      setPostType("status");
      setPostAspect("square");
      toast.success("Posted to the paddock!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally {
      setPosting(false);
    }
  };

  const discardMilestoneDraft = async () => {
    if (!milestoneDraft) return;
    try { await simhubAPI.dismissMilestone(milestoneDraft._id); } catch { /* silent */ }
    setMilestoneDraft(null);
    setContent("");
    const params = new URLSearchParams(searchParams);
    params.delete("draft");
    setSearchParams(params, { replace: true });
  };

  const handleStoryFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) { toast.error("Only images or videos are supported"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setStoryForm({ ...storyForm, media_url: ev.target.result, media_type: isVideo ? "video" : "image" });
    reader.readAsDataURL(file);
  };

  const submitStory = async () => {
    if (!storyForm.media_url) {
      toast.error("Pick a photo or video for your story");
      return;
    }
    setCreatingStory(true);
    try {
      await socialAPI.createStory({
        media_url: storyForm.media_url,
        media_type: storyForm.media_type,
        caption: storyForm.caption,
      });
      toast.success("Story posted!");
      setStoryForm({ media_url: "", media_type: "image", caption: "" });
      setStoryModal(null);
      loadStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post story");
    } finally {
      setCreatingStory(false);
    }
  };

  const onToggleSave = (postId, isSaved) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (isSaved) next.add(postId); else next.delete(postId);
      return next;
    });
  };

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[minmax(0,600px)_1fr]">
      {/* Feed column */}
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-2">
          <div>
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_throttle-hub-6/artifacts/uoo29j8n_Paddock%20Banter%20Logo.png"
              alt="Paddock Banter"
              className="mb-1 h-9 w-auto"
              data-testid="paddock-banter-logo"
            />
            <div className="relative ml-8 mt-1 inline-flex" data-testid="social-subtitle-wrap">
              <span
                aria-hidden
                className="absolute -left-3 bottom-0 top-0 z-10 flex w-8 -skew-x-[11deg] items-center justify-center overflow-hidden bg-white shadow-[0_8px_18px_-6px_rgba(0,0,0,0.6)]"
              >
                <span className="absolute inset-y-0 left-0 w-1 bg-primary" />
                <Gamepad2 className="h-4 w-4 skew-x-[11deg] text-black" />
              </span>
              <span className="-skew-x-[11deg] border border-white/20 bg-[hsl(240_6%_17%)] py-1 pl-7 pr-4 shadow-sm" data-testid="social-subtitle">
                <span className="block skew-x-[11deg] font-display text-sm font-bold italic uppercase tracking-wide text-white">
                  Sim Racing Network
                </span>
              </span>
            </div>
          </div>
          <Button
            variant={filter === "saved" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter((f) => f === "saved" ? "all" : "saved")}
            className={filter === "saved" ? "bg-primary text-primary-foreground" : ""}
            data-testid="social-saved-link"
          >
            <Bookmark className={`mr-1.5 h-4 w-4 ${filter === "saved" ? "fill-current" : ""}`} /> Saved
          </Button>
        </div>

        {/* Search */}
        <div className="relative" data-testid="social-search">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search posts, @users, or #hashtags"
            className="pl-9 pr-9 bg-secondary border-2 border-border shadow-sm focus-visible:border-primary"
            data-testid="social-search-input"
          />
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="social-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2" data-testid="social-filter-row">
          {[
            { k: "all", label: "All posts" },
            { k: "following", label: "Following", icon: Users },
            { k: "achievements", label: "Achievements", icon: Trophy },
            { k: "lap_times", label: "Lap times", icon: Gauge },
            { k: "saved", label: "Saved", icon: Bookmark },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                filter === f.k
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-secondary/40 text-muted-foreground hover:border-primary/60 hover:text-foreground"
              }`}
              data-testid={`social-filter-${f.k}`}
            >
              {f.icon ? <f.icon className="h-3.5 w-3.5" /> : null}
              {f.label}
            </button>
          ))}
        </div>

        {/* Stories bar */}
        <Card className="border border-border bg-secondary/40 p-3" data-testid="social-stories-bar">
          <div className="flex gap-4 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden">
            {/* Add-your-story button */}
            <button
              onClick={() => setStoryModal(true)}
              className="group flex flex-col items-center gap-1 focus:outline-none"
              data-testid="social-add-story"
            >
              <div className="relative h-16 w-16 flex-shrink-0">
                <div className="h-full w-full overflow-hidden rounded-full border-2 border-dashed border-primary bg-secondary">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="" className="h-full w-full object-cover opacity-70" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs font-bold text-muted-foreground">
                      {(user?.username || "U").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-card bg-primary">
                  <Plus className="h-3.5 w-3.5 text-primary-foreground" />
                </div>
              </div>
              <span className="max-w-[64px] truncate text-[11px] font-medium text-muted-foreground group-hover:text-foreground">Your story</span>
            </button>

            {stories.map((s) => (
              <button
                key={s.id}
                onClick={() => openStoryViewer(s)}
                className="group flex flex-col items-center gap-1 focus:outline-none"
                data-testid={`social-story-${s.id}`}
              >
                <div className="rounded-full bg-gradient-to-tr from-primary via-accent to-primary p-[2px]">
                  <div className="rounded-full border-2 border-card">
                    <Avatar className="h-[60px] w-[60px]">
                      <AvatarImage src={s.author_image || s.profile_image} />
                      <AvatarFallback className="bg-secondary text-sm">{(s.author_username || s.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </div>
                </div>
                <span className="max-w-[64px] truncate text-[11px] font-medium text-foreground">{s.author_username || s.username}</span>
              </button>
            ))}
            {stories.length === 0 && (
              <div className="flex items-center px-2 text-xs text-muted-foreground">
                No stories yet. Share the first one!
              </div>
            )}
          </div>
        </Card>

        {/* Composer */}
        <Card className="border-2 border-primary/30 bg-card p-4 shadow-sm ring-1 ring-primary/10">
          <div className="flex gap-3">
            <Avatar className="h-10 w-10 ring-2 ring-primary/40">
              <AvatarImage src={user?.profile_image} />
              <AvatarFallback className="bg-secondary">{(user?.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              {milestoneDraft && (
                <div className="mb-2 rounded-md border border-primary/40 bg-primary/10 p-3" data-testid="social-milestone-draft">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <Trophy className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-primary">SimHub milestone</p>
                        <p className="mt-1 text-sm font-medium">{milestoneKindLabel(milestoneDraft.kind)}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{milestoneSnapshotLine(milestoneDraft)}</p>
                      </div>
                    </div>
                    <button
                      onClick={discardMilestoneDraft}
                      className="rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      data-testid="social-milestone-discard"
                      title="Discard"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share a lap, a setup, a win..." rows={2} className="resize-none" data-testid="social-create-input" />
              {attachedImage && (
                <div className="mt-2 space-y-2">
                  <div
                    className="relative mx-auto overflow-hidden rounded-md bg-secondary"
                    style={{ aspectRatio: postAspect === "portrait" ? "4 / 5" : postAspect === "landscape" ? "16 / 9" : "1 / 1", maxWidth: postAspect === "landscape" ? "100%" : "320px" }}
                  >
                    <img src={attachedImage} alt="Attached" className="h-full w-full object-cover" />
                    <button onClick={() => setAttachedImage("")} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black" data-testid="social-remove-attached-image">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2" data-testid="social-aspect-row">
                    {[["square", "1:1"], ["portrait", "4:5"], ["landscape", "16:9"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setPostAspect(val)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${postAspect === val ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
                        data-testid={`social-aspect-${val}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <input type="file" accept="image/*" ref={imageInputRef} className="hidden" onChange={attachImage} data-testid="social-composer-image-input" />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className={`rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary ${attachedImage ? "text-primary" : ""}`}
                    data-testid="social-composer-camera"
                    title="Attach photo"
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPostType((t) => t === "achievement" ? "status" : "achievement")}
                    className={`rounded-md p-2 transition-colors hover:bg-secondary ${postType === "achievement" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
                    data-testid="social-composer-trophy"
                    title="Tag as achievement"
                  >
                    <Trophy className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPostType((t) => t === "lap_time" ? "status" : "lap_time")}
                    className={`rounded-md p-2 transition-colors hover:bg-secondary ${postType === "lap_time" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
                    data-testid="social-composer-gauge"
                    title="Tag as lap time / SimHub update"
                  >
                    <Gauge className="h-5 w-5" />
                  </button>
                  {postType !== "status" && (
                    <Badge className="ml-2 border-0 bg-primary/20 text-[10px] text-primary" data-testid="social-composer-type-badge">
                      {POST_TYPES[postType]?.label || postType}
                    </Badge>
                  )}
                </div>
                <Button onClick={createPost} disabled={posting || (!content.trim() && !attachedImage)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="social-create-submit">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Post</>}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {searchQuery.trim() ? (
          searching ? (
            <div className="space-y-5">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-lg" />)}</div>
          ) : (searchResults && searchResults.length) ? (
            <div className="space-y-5" data-testid="social-search-results">
              {searchResults.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  currentUserId={user?.id}
                  isAdmin={user?.is_admin}
                  savedIds={savedIds}
                  onChanged={() => {
                    socialAPI.getPosts({ search: searchQuery.trim() }).then((r) => setSearchResults(r.data?.posts || [])).catch(() => {});
                    load(true);
                  }}
                  onToggleSave={onToggleSave}
                  achConfigs={achConfigs}
                />
              ))}
            </div>
          ) : (
            <p className="py-16 text-center text-sm text-muted-foreground" data-testid="social-search-empty">
              No posts found for "{searchQuery.trim()}".
            </p>
          )
        ) : loading ? (
          <div className="space-y-5">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-lg" />)}</div>
        ) : posts.length || (focusId && focusPost) ? (
          (() => {
            const displayPosts = focusId && focusPost && !posts.some((p) => p.id === focusId) ? [focusPost, ...posts] : posts;
            return (
          <div className="space-y-5">
            {focusId && (
              <div className="flex items-center justify-between rounded-lg border border-border bg-card/60 px-3 py-2" data-testid="social-focus-banner">
                <span className="text-sm text-muted-foreground">Showing a shared post</span>
                <button
                  className="text-sm font-semibold text-primary hover:underline"
                  data-testid="social-focus-clear"
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.delete("post");
                    setSearchParams(next, { replace: true });
                  }}
                >
                  View full feed
                </button>
              </div>
            )}
            {displayPosts.map((p) => (
              <div
                key={p.id}
                ref={(el) => { postRefs.current[p.id] = el; }}
                data-testid={`social-post-wrap-${p.id}`}
                className={p.id === focusId ? "rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background transition-all" : ""}
              >
              <PostCard
                post={p}
                currentUserId={user?.id}
                isAdmin={user?.is_admin}
                savedIds={savedIds}
                onChanged={(s) => load(s)}
                onToggleSave={onToggleSave}
                achConfigs={achConfigs}
              />
              </div>
            ))}
          </div>
            );
          })()
        ) : filter === "saved" ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-card/60 p-10 text-center" data-testid="social-saved-empty">
            <Bookmark className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No saved posts yet</p>
            <p className="text-sm text-muted-foreground">Tap the bookmark icon on any post to save it for later.</p>
          </div>
        ) : (
          <p className="py-16 text-center text-sm text-muted-foreground">No posts yet. Be the first to share!</p>
        )}
      </div>

      {/* Sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-20 space-y-5">
          <Card className="flex items-center gap-3 border-border bg-card p-4">
            <Avatar className="h-14 w-14 ring-2 ring-primary/50">
              <AvatarImage src={user?.profile_image} />
              <AvatarFallback className="bg-secondary text-lg">{(user?.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-semibold">{user?.username}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.points ?? 0} points · {user?.seller_level || "rookie"}</p>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-accent" />
              <h3 className="font-display text-sm font-semibold uppercase tracking-wider">Top racers</h3>
            </div>
            <div className="space-y-3">
              {topRacers.map((r) => (
                <Link to={r.profile_slug ? `/u/${r.profile_slug}` : `/u/${r.username}`} key={r.user_id} className="flex items-center gap-3 rounded-md p-1 -m-1 hover:bg-secondary">
                  <span className="w-4 font-display text-sm font-bold text-muted-foreground">{r.rank}</span>
                  <Avatar className="h-8 w-8"><AvatarImage src={r.profile_image} /><AvatarFallback className="bg-secondary text-[10px]">{r.username.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <span className="flex-1 truncate text-sm">{r.username}</span>
                  <span className="text-xs font-semibold text-accent">{r.points}</span>
                </Link>
              ))}
              {!topRacers.length && <p className="text-xs text-muted-foreground">Leaderboard warming up...</p>}
            </div>
          </Card>
        </div>
      </aside>

      {/* Story create modal */}
      {storyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setStoryModal(null)}
          data-testid="social-story-modal"
        >
          <Card
            className="w-full max-w-md border-border bg-card p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Add to your story</h3>
              <button onClick={() => setStoryModal(null)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            {storyForm.media_url ? (
              storyForm.media_type === "video" ? (
                <video src={storyForm.media_url} controls className="mb-3 max-h-72 w-full rounded-md" data-testid="social-story-video-preview" />
              ) : (
                <img src={storyForm.media_url} alt="" className="mb-3 max-h-72 w-full rounded-md object-cover" />
              )
            ) : (
              <label className="mb-3 flex h-48 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 text-sm text-muted-foreground hover:border-primary">
                <div className="flex gap-3">
                  <Camera className="h-6 w-6" />
                  <Video className="h-6 w-6" />
                </div>
                <span>Pick a photo or video</span>
                <input type="file" accept="image/*,video/*" className="hidden" onChange={handleStoryFile} data-testid="social-story-file" />
              </label>
            )}
            <Input
              placeholder="Caption (optional)"
              value={storyForm.caption}
              onChange={(e) => setStoryForm({ ...storyForm, caption: e.target.value })}
              data-testid="social-story-caption"
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStoryModal(null)}>Cancel</Button>
              <Button onClick={submitStory} disabled={creatingStory || !storyForm.media_url} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="social-story-submit">
                {creatingStory ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post story"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Story viewer modal */}
      {storyViewer && (() => {
        const cur = storyViewer.stories[storyViewer.index];
        return (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
            onClick={() => setStoryViewer(null)}
            data-testid="social-story-viewer"
          >
            {/* Progress bars */}
            <div className="absolute left-0 right-0 top-0 z-10 flex gap-1 p-3">
              {storyViewer.stories.map((st, i) => (
                <div key={st.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/30">
                  <div className="h-full bg-white" style={{ width: i <= storyViewer.index ? "100%" : "0%" }} />
                </div>
              ))}
            </div>
            {/* Header */}
            <div className="absolute left-3 right-3 top-6 z-10 flex items-center justify-between">
              <span className="text-sm font-semibold text-white">{storyViewer.username}</span>
              <button
                onClick={(e) => { e.stopPropagation(); setStoryViewer(null); }}
                className="text-white/90 hover:text-white"
                data-testid="social-story-viewer-close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {/* Media */}
            <div className="relative flex h-full w-full max-w-md items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {cur.media_type === "video" ? (
                <video src={cur.media_url} controls autoPlay className="max-h-[85vh] w-full rounded-md object-contain" data-testid="social-story-viewer-video" />
              ) : (
                <img src={cur.media_url} alt="" className="max-h-[85vh] w-full rounded-md object-contain" data-testid="social-story-viewer-image" />
              )}
              {cur.caption && (
                <div className="absolute bottom-8 left-0 right-0 px-4 text-center">
                  <p className="inline-block rounded-md bg-black/50 px-3 py-1.5 text-sm text-white">{cur.caption}</p>
                </div>
              )}
              {/* Tap zones */}
              <button onClick={storyViewerPrev} className="absolute left-0 top-0 h-full w-1/3 focus:outline-none" aria-label="Previous" data-testid="social-story-viewer-prev" />
              <button onClick={storyViewerNext} className="absolute right-0 top-0 h-full w-1/3 focus:outline-none" aria-label="Next" data-testid="social-story-viewer-next" />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
